import React, { useState, useCallback } from 'react';
import AsyncSelect from "react-select/async";
import { useAppDispatch } from "../../../app/store";
import { ADMIN_LANG } from "../../../settings";
import { PlainDetails } from "../../../types/admin";
import { getPlainGames } from "../../../app/slices/admin";

interface AsyncGameSelectProps {
    onChange: (value: PlainDetails) => void;
    initialValue: PlainDetails | null;
    defaultOptions: PlainDetails[] | [];
    debounceTimeout?: number;
}

const AsyncGameSelect = ({ onChange, initialValue, defaultOptions, debounceTimeout = 300 }: AsyncGameSelectProps) => {
    const dispatch = useAppDispatch();
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null); // To store the timeout ID

    const loadOptions = (input: string) => {
        return new Promise<PlainDetails[]>((resolve) => {
            if (input.length < 2) {
                return resolve([]);
            }

            dispatch(getPlainGames(input))
                .then((fulfilledAction) => {
                    const data: PlainDetails[] = fulfilledAction.payload.games;
                    resolve(data);
                })
                .catch(() => resolve([]));
        });
    };

    const debouncedLoadOptions = useCallback(
        (inputValue: string, callback: (options: PlainDetails[]) => void) => {
            if (timer) {
                clearTimeout(timer); // Clear the previous timer if input is typed within the debounceTimeout
            }

            const newTimer = setTimeout(() => {
                loadOptions(inputValue).then(callback);
            }, debounceTimeout);

            setTimer(newTimer); // Save the new timer
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [debounceTimeout, timer]
    );

    return (
        <AsyncSelect
            value={initialValue}
            cacheOptions
            defaultOptions={defaultOptions}
            loadOptions={debouncedLoadOptions}
            noOptionsMessage={() => ADMIN_LANG.search}
            onChange={(selectedValue) => onChange(selectedValue as PlainDetails)}
            placeholder={ADMIN_LANG.select_game}
            classNamePrefix="react-select"
            className="custom-react-select"
        />
    );
};

export default AsyncGameSelect;